import { formatYmdAsCrmDate } from '../utils/dateTime';
import ExcelJS from 'exceljs';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { downloadWorkbook } from './projectExport';
import { fetchDemUserDisplayNameMap, resolveDemDisplayUser } from './demUserNames';
import type {
  DemAnalyticsCffRow,
  DemAnalyticsCffTotals,
  DemAnalyticsChannelTotals,
  DemAnalyticsDeliveryRow,
  DemAnalyticsDeliveryTotals,
  DemAnalyticsLeaderboardRow,
  DemAnalyticsMonthVolume,
  DemAnalyticsOrder,
  DemAnalyticsPersonCount,
  DemAnalyticsStatusSlice,
  DemAnalyticsHourlyChannelPoint,
  DemAnalyticsTrendPoint,
  DemAnalyticsUserKpi,
  DemAnalyticsYearTotal,
} from '../types/dem';

const SGT = 'Asia/Singapore';
const ORDER_SELECT =
  'order_id, created_at, customer_code, status, source, created_by, exported_by, processing_seconds';
const PAGE_SIZE = 1000;
/** Dashboard charts only need recent history; older years stay out of the hot path. */
const ANALYTICS_FROM_YEAR = 2024;
const ANALYTICS_CACHE_MS = 90_000;
const ORDER_FETCH_CONCURRENCY = 6;

const STATUS_COLORS: Record<string, string> = {
  Imported: '#00b894',
  Processed: '#00b894',
  Pending: '#f39c12',
  Cancelled: '#ff7675',
  Void: '#ff7675',
  Archived: '#bdc3c7',
  Uploading: '#3498db',
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export function singaporeYmd(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SGT,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function singaporeYearMonth(date: Date = new Date()): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SGT,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  return {
    year: Number(parts.find((p) => p.type === 'year')?.value ?? '0'),
    month: Number(parts.find((p) => p.type === 'month')?.value ?? '1'),
  };
}

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = Date.UTC(y, m - 1, d + days, 12);
  return singaporeYmd(new Date(utc));
}

export function formatDisplayDate(ymd: string): string {
  return formatYmdAsCrmDate(ymd);
}

export function monthLabel(month: number): string {
  return MONTH_LABELS[month - 1] ?? String(month);
}

function parseCreatedAt(raw: string | null | undefined): Date {
  if (!raw) return new Date();
  const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : raw.replace(' ', 'T') + '+00:00';
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function enrichDateParts(date: Date) {
  const dateOnly = singaporeYmd(date);
  const { year, month } = singaporeYearMonth(date);
  return {
    dateOnly,
    dayStr: dateOnly,
    monthStr: dateOnly.slice(0, 7),
    year,
    month,
  };
}

function resolveDisplayUser(
  rawUser: string,
  source: string,
  nameMap: Map<string, string>,
): { userKey: string; user: string } {
  if (!rawUser && source === 'Web') {
    return { userKey: 'unassigned', user: 'Unassigned' };
  }

  const raw = rawUser || 'System';
  const base = raw.split('(')[0].trim() || 'System';
  const userKey =
    base.toLowerCase().replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim() || base.toLowerCase();
  const user = resolveDemDisplayUser(raw, nameMap);

  return { userKey, user };
}

function formatCustomerLabel(code: string, customerNames: Map<string, string>): string {
  if (!code) return 'Unknown';
  const name = customerNames.get(code);
  return name ? `[${code}] ${name}` : `[${code}]`;
}

function rowToAnalyticsOrder(
  row: Record<string, unknown>,
  customerNames: Map<string, string>,
  userNames: Map<string, string>,
): DemAnalyticsOrder | null {
  try {
    const date = parseCreatedAt(String(row.created_at ?? ''));
    const parts = enrichDateParts(date);
    const source = String(row.source ?? 'Desktop');
    const rawUser =
      source === 'Web'
        ? String(row.exported_by ?? '')
        : String(row.created_by ?? row.exported_by ?? '');
    const code = String(row.customer_code ?? '');
    const { userKey, user } = resolveDisplayUser(rawUser, source, userNames);
    const rawSeconds = row.processing_seconds;
    let processingSeconds: number | null = null;
    if (rawSeconds != null && rawSeconds !== '') {
      const n = Number(rawSeconds);
      if (Number.isFinite(n) && n > 0) processingSeconds = Math.floor(n);
    }

    return {
      date,
      ...parts,
      orderId: String(row.order_id ?? ''),
      customer: formatCustomerLabel(code, customerNames),
      status: String(row.status ?? 'Unknown'),
      userKey,
      user,
      source,
      processingSeconds,
    };
  } catch {
    return null;
  }
}

async function fetchCustomerNameMap(): Promise<Map<string, string>> {
  // Kept for optional export enrichment; dashboard load no longer waits on 16k+ customers.
  const db = getDbSupabase('dem');
  const map = new Map<string, string>();
  let offset = 0;

  while (true) {
    const { data, error } = await db
      .from('customers')
      .select('code, name')
      .order('code')
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) throw new Error(formatSupabaseError(error));

    const rows = data ?? [];
    for (const row of rows) {
      const code = String((row as Record<string, unknown>).code ?? '');
      const name = String((row as Record<string, unknown>).name ?? '');
      if (code) map.set(code, name);
    }

    if (rows.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return map;
}

let analyticsOrdersCache: { at: number; orders: DemAnalyticsOrder[] } | null = null;
let analyticsDeliveriesCache: { at: number; rows: DemAnalyticsDeliveryRow[] } | null = null;
let analyticsCffCache: { at: number; rows: DemAnalyticsCffRow[] } | null = null;
let analyticsOrdersInflight: Promise<DemAnalyticsOrder[]> | null = null;
let analyticsDeliveriesInflight: Promise<DemAnalyticsDeliveryRow[]> | null = null;
let analyticsCffInflight: Promise<DemAnalyticsCffRow[]> | null = null;

/** Prefetch dashboard datasets into the shared in-flight/cache (safe to call early). */
export function warmDemAnalyticsCache(): void {
  void fetchDemAnalyticsOrders().catch(() => {
    /* warm-up */
  });
  void fetchDemAnalyticsDeliveries().catch(() => {
    /* warm-up */
  });
  void fetchDemAnalyticsCff().catch(() => {
    /* warm-up */
  });
}

async function fetchOrderPage(
  fromYear: number,
  page: number,
): Promise<Record<string, unknown>[]> {
  const db = getDbSupabase('dem');
  const from = page * PAGE_SIZE;
  const { data, error } = await db
    .from('orders')
    .select(ORDER_SELECT)
    .gte('created_at', `${fromYear}-01-01`)
    .order('created_at', { ascending: false })
    .range(from, from + PAGE_SIZE - 1);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as Record<string, unknown>[];
}

export async function fetchDemAnalyticsOrders(
  options?: { force?: boolean },
): Promise<DemAnalyticsOrder[]> {
  const force = Boolean(options?.force);
  if (
    !force &&
    analyticsOrdersCache &&
    Date.now() - analyticsOrdersCache.at < ANALYTICS_CACHE_MS
  ) {
    return analyticsOrdersCache.orders;
  }
  if (!force && analyticsOrdersInflight) return analyticsOrdersInflight;

  const run = (async () => {
    const db = getDbSupabase('dem');
    const fromYear = ANALYTICS_FROM_YEAR;
    const emptyCustomers = new Map<string, string>();
    const concurrency = ORDER_FETCH_CONCURRENCY;

    const [userNames, countRes] = await Promise.all([
      fetchDemUserDisplayNameMap(),
      db
        .from('orders')
        .select('order_id', { count: 'exact', head: true })
        .gte('created_at', `${fromYear}-01-01`),
    ]);
    if (countRes.error) throw new Error(formatSupabaseError(countRes.error));

    const total = Math.max(0, countRes.count ?? 0);
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const rows: Record<string, unknown>[] = [];

    for (let start = 0; start < pageCount; start += concurrency) {
      const pageIndexes = Array.from(
        { length: Math.min(concurrency, pageCount - start) },
        (_, i) => start + i,
      );
      const pages = await Promise.all(pageIndexes.map((page) => fetchOrderPage(fromYear, page)));
      for (const pageRows of pages) rows.push(...pageRows);
    }

    const orders: DemAnalyticsOrder[] = [];
    for (const row of rows) {
      const parsed = rowToAnalyticsOrder(row, emptyCustomers, userNames);
      if (parsed) orders.push(parsed);
    }

    analyticsOrdersCache = { at: Date.now(), orders };
    return orders;
  })();

  analyticsOrdersInflight = run;
  try {
    return await run;
  } finally {
    if (analyticsOrdersInflight === run) analyticsOrdersInflight = null;
  }
}

/** Warm customer names into export rows (optional; does not block dashboard). */
export async function enrichAnalyticsOrdersForExport(
  orders: DemAnalyticsOrder[],
): Promise<DemAnalyticsOrder[]> {
  try {
    const names = await fetchCustomerNameMap();
    if (names.size === 0) return orders;
    return orders.map((order) => {
      const codeMatch = order.customer.match(/^\[([^\]]+)\]/);
      const code = codeMatch?.[1]?.trim() ?? '';
      if (!code) return order;
      return { ...order, customer: formatCustomerLabel(code, names) };
    });
  } catch {
    return orders;
  }
}

function isVoidUser(user: string): boolean {
  return /\(Void\)/i.test(user);
}

function isCancelledStatus(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'cancelled' || s === 'void';
}

/** Void button sets status=Cancelled + user "(Void)". Same bucket for analytics. */
function isCancelledOrder(order: { status: string; user: string }): boolean {
  return isCancelledStatus(order.status) || isVoidUser(order.user);
}

function isProcessedStatus(status: string): boolean {
  const s = status.trim();
  // Archived = cleaned-up imported orders (never Cancelled — archive skips those).
  return s === 'Processed' || s === 'Archived';
}

function isImportedOrder(order: { status: string; user: string }): boolean {
  return isProcessedStatus(order.status) && !isCancelledOrder(order);
}

export type DemAnalyticsChannel = 'po' | 'manual' | 'b2b' | 'whatsapp';

export function mapOrderChannel(source: string): DemAnalyticsChannel {
  const normalized = source.trim().toLowerCase();
  if (normalized === 'whatsapp') return 'whatsapp';
  if (normalized === 'web') return 'b2b';
  // Manual Order module stores source = CallIn (sequence prefix MI).
  if (normalized === 'callin' || normalized === 'call_in' || normalized === 'manual') return 'manual';
  return 'po';
}

export function channelLabel(channel: DemAnalyticsChannel): string {
  if (channel === 'manual') return 'Manual';
  if (channel === 'b2b') return 'B2B';
  if (channel === 'whatsapp') return 'WhatsApp';
  return 'PO';
}

export function filterOrdersByDateRange(
  orders: DemAnalyticsOrder[],
  startYmd: string,
  endYmd: string,
): DemAnalyticsOrder[] {
  return orders.filter((o) => o.dateOnly >= startYmd && o.dateOnly <= endYmd);
}

export function filterOrdersByDate(orders: DemAnalyticsOrder[], ymd: string): DemAnalyticsOrder[] {
  return orders.filter((o) => o.dateOnly === ymd);
}

export function filterOrdersByYearMonth(
  orders: DemAnalyticsOrder[],
  year: number,
  month: number,
): DemAnalyticsOrder[] {
  return orders.filter((o) => o.year === year && o.month === month);
}

export function filterOrdersByYear(orders: DemAnalyticsOrder[], year: number): DemAnalyticsOrder[] {
  return orders.filter((o) => o.year === year);
}

export function buildTrendByLabel(
  orders: DemAnalyticsOrder[],
  labelFor: (order: DemAnalyticsOrder) => string,
): DemAnalyticsTrendPoint[] {
  const map = new Map<string, DemAnalyticsTrendPoint>();

  for (const order of orders) {
    const label = labelFor(order);
    const entry = map.get(label) ?? { label, processed: 0, cancelled: 0 };
    if (isImportedOrder(order)) entry.processed += 1;
    else if (isCancelledOrder(order)) entry.cancelled += 1;
    map.set(label, entry);
  }

  return [...map.values()];
}

export function buildLast7DaysTrend(
  orders: DemAnalyticsOrder[],
  endYmd: string,
): DemAnalyticsTrendPoint[] {
  const startYmd = addDaysYmd(endYmd, -6);
  const recent = filterOrdersByDateRange(orders, startYmd, endYmd);
  const points = buildTrendByLabel(recent, (o) => o.dayStr);
  const ordered: DemAnalyticsTrendPoint[] = [];

  for (let i = 0; i < 7; i += 1) {
    const ymd = addDaysYmd(startYmd, i);
    const existing = points.find((p) => p.label === ymd);
    ordered.push(existing ?? { label: ymd, processed: 0, cancelled: 0 });
  }

  return ordered;
}

/** Hour of day in Asia/Singapore (0–23). */
export function singaporeHour(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SGT,
    hour: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  // Some engines emit "24" for midnight under hour12:false.
  if (!Number.isFinite(hour) || hour < 0) return 0;
  return hour === 24 ? 0 : hour % 24;
}

export function hourlyChannelTotal(point: DemAnalyticsHourlyChannelPoint): number {
  return point.po + point.manual + point.b2b + point.whatsapp;
}

/**
 * Orders by clock hour (SGT) × channel, using order created_at.
 * Skips cancelled/void. Always returns 24 buckets.
 */
export function buildHourlyChannelTrend(
  orders: DemAnalyticsOrder[],
): DemAnalyticsHourlyChannelPoint[] {
  const buckets: DemAnalyticsHourlyChannelPoint[] = Array.from({ length: 24 }, (_, hour) => ({
    label: String(hour).padStart(2, '0'),
    po: 0,
    manual: 0,
    b2b: 0,
    whatsapp: 0,
  }));

  for (const order of orders) {
    if (isCancelledOrder(order)) continue;
    const entry = buckets[singaporeHour(order.date)]!;
    entry[mapOrderChannel(order.source)] += 1;
  }

  return buckets;
}

/** e.g. "Peak 10:00 · 23 orders" for card subtitle. */
export function peakHourSummary(points: DemAnalyticsHourlyChannelPoint[]): string | null {
  let bestLabel = '';
  let bestTotal = 0;
  for (const point of points) {
    const total = hourlyChannelTotal(point);
    if (total > bestTotal) {
      bestTotal = total;
      bestLabel = point.label;
    }
  }
  if (bestTotal <= 0 || !bestLabel) return null;
  return `Peak ${bestLabel}:00 · ${bestTotal.toLocaleString()} order${bestTotal === 1 ? '' : 's'}`;
}

export function buildMonthlyTrendForYear(
  orders: DemAnalyticsOrder[],
  year: number,
): DemAnalyticsTrendPoint[] {
  const yearOrders = filterOrdersByYear(orders, year);
  const points = buildTrendByLabel(yearOrders, (o) => o.monthStr);
  const ordered: DemAnalyticsTrendPoint[] = [];

  for (let month = 1; month <= 12; month += 1) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const existing = points.find((p) => p.label === key);
    ordered.push(
      existing ?? {
        label: key,
        processed: 0,
        cancelled: 0,
      },
    );
  }

  return ordered;
}

export function buildUserKpi(
  orders: DemAnalyticsOrder[],
  includeCancelled: boolean,
): DemAnalyticsUserKpi[] {
  const map = new Map<string, DemAnalyticsUserKpi>();

  for (const order of orders) {
    const displayUser = order.user.replace(/\s*\(Void\)\s*/gi, '').trim() || order.user;
    const entry = map.get(order.userKey) ?? {
      user: displayUser,
      processed: 0,
      cancelled: 0,
      po: 0,
      manual: 0,
      b2b: 0,
      whatsapp: 0,
      processingSeconds: 0,
      timedOrders: 0,
    };
    if (!entry.user || isVoidUser(entry.user)) entry.user = displayUser;

    let touched = false;
    if (isImportedOrder(order)) {
      entry.processed += 1;
      entry[mapOrderChannel(order.source)] += 1;
      touched = true;
    } else if (includeCancelled && isCancelledOrder(order)) {
      entry.cancelled += 1;
      touched = true;
    }

    // Processing time is recorded at Save (often while still Pending) and must
    // count for monthly/yearly even when the order is Pending / Cancelled.
    if (order.processingSeconds != null && order.processingSeconds > 0) {
      entry.processingSeconds += order.processingSeconds;
      entry.timedOrders += 1;
      touched = true;
    }

    if (touched) map.set(order.userKey, entry);
  }

  return [...map.values()].sort((a, b) => {
    const totalA = a.processed + a.cancelled;
    const totalB = b.processed + b.cancelled;
    if (totalB !== totalA) return totalB - totalA;
    if (b.processingSeconds !== a.processingSeconds) return b.processingSeconds - a.processingSeconds;
    return a.user.localeCompare(b.user);
  });
}

export function buildChannelTotals(
  orders: DemAnalyticsOrder[],
  includeCancelled: boolean,
): DemAnalyticsChannelTotals {
  const totals: DemAnalyticsChannelTotals = { po: 0, manual: 0, b2b: 0, whatsapp: 0, cancelled: 0 };

  for (const order of orders) {
    if (isImportedOrder(order)) {
      totals[mapOrderChannel(order.source)] += 1;
    } else if (includeCancelled && isCancelledOrder(order)) {
      totals.cancelled += 1;
    }
  }

  return totals;
}

export function buildYearTotals(orders: DemAnalyticsOrder[]): DemAnalyticsYearTotal[] {
  const map = new Map<number, number>();

  for (const order of orders) {
    if (!isImportedOrder(order)) continue;
    map.set(order.year, (map.get(order.year) ?? 0) + 1);
  }

  return [...map.entries()]
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => a.year - b.year);
}

export function buildYearLeaderboard(
  orders: DemAnalyticsOrder[],
  year: number,
): DemAnalyticsLeaderboardRow[] {
  const map = new Map<string, DemAnalyticsLeaderboardRow>();

  for (const order of orders) {
    if (order.year !== year || !isImportedOrder(order)) continue;

    const displayUser = order.user.replace(/\s*\(Void\)\s*/gi, '').trim() || order.user;
    const entry = map.get(order.userKey) ?? {
      user: displayUser,
      count: 0,
      po: 0,
      manual: 0,
      b2b: 0,
      whatsapp: 0,
    };
    entry.count += 1;
    entry[mapOrderChannel(order.source)] += 1;
    map.set(order.userKey, entry);
  }

  return [...map.values()].sort((a, b) => b.count - a.count || a.user.localeCompare(b.user));
}

export function buildStatusBreakdown(
  orders: DemAnalyticsOrder[],
  year: number,
): DemAnalyticsStatusSlice[] {
  const map = new Map<string, number>();

  for (const order of orders) {
    if (order.year !== year) continue;
    let label = order.status;
    if (isCancelledOrder(order)) label = 'Cancelled';
    else if (label === 'Processed' || label === 'Archived') label = 'Imported';
    map.set(label, (map.get(label) ?? 0) + 1);
  }

  return [...map.entries()]
    .map(([label, count]) => ({
      label,
      count,
      color: STATUS_COLORS[label] ?? '#bdc3c7',
    }))
    .sort((a, b) => b.count - a.count);
}

export function trendLabelShort(label: string, mode: 'day' | 'month'): string {
  if (mode === 'month') {
    const month = Number(label.slice(5, 7));
    return monthLabel(month);
  }
  const [, m, d] = label.split('-').map(Number);
  return `${monthLabel(m)} ${d}`;
}

export async function exportDemAnalyticsOrders(orders: DemAnalyticsOrder[]): Promise<void> {
  const enriched = await enrichAnalyticsOrdersForExport(orders);
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet('Orders');

  sheet.columns = [
    { header: 'Date', key: 'date', width: 20 },
    { header: 'Order_ID', key: 'orderId', width: 18 },
    { header: 'Customer', key: 'customer', width: 36 },
    { header: 'Source', key: 'source', width: 12 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'User', key: 'user', width: 24 },
    { header: 'Processing_seconds', key: 'processingSeconds', width: 18 },
  ];

  sheet.getRow(1).font = { bold: true };

  for (const order of enriched) {
    sheet.addRow({
      date: new Intl.DateTimeFormat('en-GB', {
        timeZone: SGT,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(order.date),
      orderId: order.orderId,
      customer: order.customer,
      source: order.source,
      status: order.status,
      user: order.user,
      processingSeconds: order.processingSeconds ?? '',
    });
  }

  const stamp = new Intl.DateTimeFormat('en-CA', {
    timeZone: SGT,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(new Date())
    .replace(/[^\d]/g, '');

  await downloadWorkbook(wb, `All_Orders_Report_${stamp}.xlsx`);
}

export function yearOptions(from = ANALYTICS_FROM_YEAR): number[] {
  const current = singaporeYearMonth().year;
  const years: number[] = [];
  for (let y = from; y <= current + 1; y += 1) years.push(y);
  return years;
}

type DatedAnalyticsRow = {
  dateOnly: string;
  monthStr: string;
  year: number;
};

function filterDatedByDate<T extends DatedAnalyticsRow>(rows: T[], ymd: string): T[] {
  return rows.filter((row) => row.dateOnly === ymd);
}

function filterDatedByYearMonth<T extends DatedAnalyticsRow>(
  rows: T[],
  year: number,
  month: number,
): T[] {
  const monthStr = `${year}-${String(month).padStart(2, '0')}`;
  return rows.filter((row) => row.monthStr === monthStr);
}

function filterDatedByYear<T extends DatedAnalyticsRow>(rows: T[], year: number): T[] {
  return rows.filter((row) => row.year === year);
}

function deliveryBatchKey(row: {
  id: string;
  batch_id: string | null;
  fulfillment_type: string;
  lalamove_order_id: string | null;
}): string {
  const batch = row.batch_id?.trim();
  if (batch) return batch;
  if (row.fulfillment_type === 'lalamove') {
    const orderId = row.lalamove_order_id?.trim();
    if (orderId) return orderId;
  }
  return row.id;
}

export async function fetchDemAnalyticsDeliveries(
  options?: { force?: boolean },
): Promise<DemAnalyticsDeliveryRow[]> {
  const force = Boolean(options?.force);
  if (
    !force &&
    analyticsDeliveriesCache &&
    Date.now() - analyticsDeliveriesCache.at < ANALYTICS_CACHE_MS
  ) {
    return analyticsDeliveriesCache.rows;
  }
  if (!force && analyticsDeliveriesInflight) return analyticsDeliveriesInflight;

  const run = (async () => {
    const { data, error } = await getDbSupabase('dem')
      .from('delivery_arrangements')
      .select(
        'id, batch_id, stop_sequence, fulfillment_type, created_at, created_by, requested_by, issue_caused_by, lalamove_order_id',
      )
      .order('created_at', { ascending: false });
    if (error) throw new Error(formatSupabaseError(error));

    const byBatch = new Map<
      string,
      Array<{
        id: string;
        batch_id: string | null;
        stop_sequence: number;
        fulfillment_type: string;
        created_at: string;
        created_by: string;
        requested_by: string | null;
        issue_caused_by: string | null;
        lalamove_order_id: string | null;
      }>
    >();

    for (const raw of data ?? []) {
      const row = {
        id: String(raw.id),
        batch_id: raw.batch_id != null ? String(raw.batch_id) : null,
        stop_sequence: Number(raw.stop_sequence ?? 1),
        fulfillment_type: String(raw.fulfillment_type ?? 'internal'),
        created_at: String(raw.created_at ?? ''),
        created_by: String(raw.created_by ?? ''),
        requested_by: raw.requested_by != null ? String(raw.requested_by) : null,
        issue_caused_by: raw.issue_caused_by != null ? String(raw.issue_caused_by) : null,
        lalamove_order_id: raw.lalamove_order_id != null ? String(raw.lalamove_order_id) : null,
      };
      const key = deliveryBatchKey(row);
      const list = byBatch.get(key);
      if (list) list.push(row);
      else byBatch.set(key, [row]);
    }

    const rows: DemAnalyticsDeliveryRow[] = [];
    for (const stops of byBatch.values()) {
      const sorted = [...stops].sort((a, b) => {
        const seq = a.stop_sequence - b.stop_sequence;
        if (seq !== 0) return seq;
        return parseCreatedAt(a.created_at).getTime() - parseCreatedAt(b.created_at).getTime();
      });
      const head = sorted.find((row) => row.stop_sequence <= 1) ?? sorted[0]!;
      const parts = enrichDateParts(parseCreatedAt(head.created_at));
      rows.push({
        dateOnly: parts.dateOnly,
        monthStr: parts.monthStr,
        year: parts.year,
        month: parts.month,
        fulfillmentType: head.fulfillment_type === 'lalamove' ? 'lalamove' : 'internal',
        createdBy: head.created_by.trim() || 'Unspecified',
        requestedBy: head.requested_by?.trim() || 'Unspecified',
        issueCausedBy: head.issue_caused_by?.trim() || 'Unspecified',
      });
    }

    analyticsDeliveriesCache = { at: Date.now(), rows };
    return rows;
  })();

  analyticsDeliveriesInflight = run;
  try {
    return await run;
  } finally {
    if (analyticsDeliveriesInflight === run) analyticsDeliveriesInflight = null;
  }
}

export async function fetchDemAnalyticsCff(
  options?: { force?: boolean },
): Promise<DemAnalyticsCffRow[]> {
  const force = Boolean(options?.force);
  if (
    !force &&
    analyticsCffCache &&
    Date.now() - analyticsCffCache.at < ANALYTICS_CACHE_MS
  ) {
    return analyticsCffCache.rows;
  }
  if (!force && analyticsCffInflight) return analyticsCffInflight;

  const run = (async () => {
    const { data, error } = await getDbSupabase('forms')
      .from('customer_feedback_submissions')
      .select('id, submitted_at, submitted_by, form_data')
      .order('submitted_at', { ascending: false });
    if (error) throw new Error(formatSupabaseError(error));

    const rows: DemAnalyticsCffRow[] = [];
    for (const raw of data ?? []) {
      const form =
        raw.form_data && typeof raw.form_data === 'object'
          ? (raw.form_data as Record<string, unknown>)
          : {};
      const requestRaw = String(form.request_type ?? '').trim().toLowerCase();
      const requestType =
        requestRaw === 'return' || requestRaw === 'exchange' || requestRaw === 'replenish'
          ? requestRaw
          : '';
      const submittedBy =
        String(form.submitted_by ?? '').trim() ||
        String(raw.submitted_by ?? '').trim() ||
        'Unknown';
      const parts = enrichDateParts(parseCreatedAt(String(raw.submitted_at ?? '')));
      rows.push({
        dateOnly: parts.dateOnly,
        monthStr: parts.monthStr,
        year: parts.year,
        month: parts.month,
        requestType,
        submittedBy,
      });
    }

    analyticsCffCache = { at: Date.now(), rows };
    return rows;
  })();

  analyticsCffInflight = run;
  try {
    return await run;
  } finally {
    if (analyticsCffInflight === run) analyticsCffInflight = null;
  }
}

export function filterDeliveriesByDate(
  rows: DemAnalyticsDeliveryRow[],
  ymd: string,
): DemAnalyticsDeliveryRow[] {
  return filterDatedByDate(rows, ymd);
}

export function filterDeliveriesByYearMonth(
  rows: DemAnalyticsDeliveryRow[],
  year: number,
  month: number,
): DemAnalyticsDeliveryRow[] {
  return filterDatedByYearMonth(rows, year, month);
}

export function filterDeliveriesByYear(
  rows: DemAnalyticsDeliveryRow[],
  year: number,
): DemAnalyticsDeliveryRow[] {
  return filterDatedByYear(rows, year);
}

export function filterCffByDate(rows: DemAnalyticsCffRow[], ymd: string): DemAnalyticsCffRow[] {
  return filterDatedByDate(rows, ymd);
}

export function filterCffByYearMonth(
  rows: DemAnalyticsCffRow[],
  year: number,
  month: number,
): DemAnalyticsCffRow[] {
  return filterDatedByYearMonth(rows, year, month);
}

export function filterCffByYear(rows: DemAnalyticsCffRow[], year: number): DemAnalyticsCffRow[] {
  return filterDatedByYear(rows, year);
}

export function buildDeliveryTotals(rows: DemAnalyticsDeliveryRow[]): DemAnalyticsDeliveryTotals {
  let lalamove = 0;
  let internal = 0;
  for (const row of rows) {
    if (row.fulfillmentType === 'lalamove') lalamove += 1;
    else internal += 1;
  }
  return { total: rows.length, lalamove, internal };
}

export function buildCffTotals(rows: DemAnalyticsCffRow[]): DemAnalyticsCffTotals {
  let returns = 0;
  let exchanges = 0;
  let replenish = 0;
  for (const row of rows) {
    if (row.requestType === 'return') returns += 1;
    else if (row.requestType === 'exchange') exchanges += 1;
    else if (row.requestType === 'replenish') replenish += 1;
  }
  return { total: rows.length, returns, exchanges, replenish };
}

function buildPersonCounts(values: string[]): DemAnalyticsPersonCount[] {
  const map = new Map<string, number>();
  for (const value of values) {
    const person = value.trim() || 'Unspecified';
    map.set(person, (map.get(person) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([person, total]) => ({ person, total }))
    .sort(
      (a, b) =>
        b.total - a.total || a.person.localeCompare(b.person, undefined, { sensitivity: 'base' }),
    );
}

export function buildDeliveryCreatorStats(rows: DemAnalyticsDeliveryRow[]) {
  return buildPersonCounts(rows.map((row) => row.createdBy));
}

export function buildDeliveryRequestedByStats(rows: DemAnalyticsDeliveryRow[]) {
  return buildPersonCounts(rows.map((row) => row.requestedBy));
}

export function buildDeliveryIssueCausedByStats(rows: DemAnalyticsDeliveryRow[]) {
  return buildPersonCounts(rows.map((row) => row.issueCausedBy));
}

export function buildCffPersonStats(rows: DemAnalyticsCffRow[]) {
  return buildPersonCounts(rows.map((row) => row.submittedBy));
}

export function buildDeliveryMonthVolumes(
  rows: DemAnalyticsDeliveryRow[],
  year: number,
): DemAnalyticsMonthVolume[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (row.year !== year) continue;
    map.set(row.monthStr, (map.get(row.monthStr) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([yearMonth, total]) => ({ yearMonth, total }))
    .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
}
